<template lang="pug">
    .vertical
        loading(:active="loading || fetching" :is-full-page="false" :height="40" :width="40" color="#e30018")
        .teasers(v-if="loading")
            .teaser.skeleton(v-for="teaser in teasers")
                .image
                .text
        .paginated-teasers(v-else-if="teasers && teasers.length")
            .teasers
                .teaser(v-for="teaser in teasers")
                    Teaser(:teaser="teaser" :show-topic="!section.eventCategories.length || section.eventCategories.length > 1")
                        template(slot="booking-link" v-if="isActiveBooking(teaser)")
                            a(href="#" @click.stop.prevent="showModal(teaser)") Anmelden
        .notification.is-light(v-else)
            | Derzeit gibt es keine kommenden Events
        Pagination(
            v-if="pageInfo && pageInfo.lastPage > 1"
            :last-page="pageInfo.lastPage"
            :current-page="pageInfo.currentPage"
            nav-class="pagination is-rounded"
            @change="load"
        )
            span.icon(slot="prev")
                i.fas.fa-arrow-left
            span.icon(slot="next")
                i.fas.fa-arrow-right
        portal(to="modals")
            transition(name="modal")
                FormModal(
                    v-if="modal.visible && modal.slot"
                    :data="modal.slot"
                    :force-show-form="true"
                    @close="hideModal"
                    @success="forceReload"
                )
</template>

<script>

import _ from 'lodash'

import { mapGetters, mapMutations, mapActions } from 'vuex'
import { isPast, format } from 'date-fns'
import { timestamp2date } from '@/filters';
import apolloClient from '@/store/apollo'

import Teaser from './Teaser'
import FormModal from './FormModal'

import Pagination from '@/components/Pagination'

import Loading from 'vue-loading-overlay'

export default {
    name: 'TeaserList',
    props: {
        section: {
            type: Object,
            required: true,
        },
        perPage: {
            type: Number,
            default: 15,
        },
    },
    data: () => ({
        fetching: false,
        modal: {
            visible: false,
            slot: undefined,
        }
    }),
    components: {
        Loading,
        Teaser,
        FormModal,
        Pagination,
    },
    computed: {
        s() {
            return _.findIndex(this.sections, { id: this.section.id })
        },
        timespan() {
            let timespan = 'auto';
            const settings = this.section.settings || [];
            const setting = settings.find(el => el.key === 'timespan');
            if (setting) timespan = setting.value;
            return timespan;
        },
        loading() {
            return _.get(this.section, 'teasers.loading', true)
        },
        teasers() {
            let teasers = []
            if (this.loading) teasers = new Array(3)
            else teasers = _.get(this.section, 'teasers.pagination.items')
            return teasers
        },
        filters() {
            return _.get(this.section, 'teasers.pagination.filters')
        },
        pageInfo() {
            return _.get(this.section, 'teasers.pagination.pageInfo')
        },
        ...mapGetters({
            sections: 'currentPage/sections',
            cms: 'CMS',
        })
    },
    methods: {
        forceReload() {
            apolloClient.cache.reset();

            let pagination = _.get(this.section, 'teasers.pagination') || {}
            let page = _.get(pagination, 'pageInfo.currentPage') || 1

            this.hideModal()
            this.load(page, true)
        },
        load(page = 1, force = false) {
            this.fetching = true;

            let pagination = _.get(this.section, 'teasers.pagination')

            // default timespan setting is 'auto'
            // it is equal to 'future' for this display type
            let key = 'from';
            let direction = 'asc';
            if (this.timespan === 'past') {
                key = 'to';
                direction = 'desc';
            }
            let timestampFilter = { key }
            timestampFilter[key] = format(new Date, 't')

            if (! pagination) {
                const categories = this.section.eventCategories.map(category => category.id)
                pagination = {
                    filters: [
                        timestampFilter,
                        { key: 'categories', in: categories },
                        { key: 'cms', match: this.cms.id }
                    ],
                    direction,
                    pageInfo: {
                        currentPage: 1,
                        lastPage: 1,
                        total: 0,
                        perPage: this.perPage
                    },
                }
            } else {
                const i = _.findIndex(pagination.filters, { key });
                if (i >= 0) {
                    if (force === false && pagination.filters[i][key] === timestampFilter[key]) return
                    pagination.filters[i] = timestampFilter
                } else {
                    pagination.filters.push(timestampFilter)
                }

                // remove filter related to calendar display type
                const x = _.findIndex(pagination.filters, { key: 'inclusive_timespan' });
                if (x >= 0) pagination.filters.splice(x, 1);
                pagination.pageInfo.currentPage = page
            }

            this.fetchTeasers(pagination)
                .then(({ items, pageInfo }) => {
                    let filters = _.cloneDeep(pagination.filters);
                    const teasers = {
                        loading: false,
                        pagination: { filters, direction, items, pageInfo }
                    };
                    this.insertTeasers({ s: this.s, teasers });
                })
                .finally(() => (this.fetching = false));
        },
        isActiveBooking(slot) {
            let active = true

            const booking = _.get(slot, 'event.booking') || 'none'
            if (active && booking === 'none') active = false

            const restriction = _.get(slot, 'event.restriction') || 'none'
            if (active && restriction !== 'none') active = false

            const limit = slot.member_limit || slot.event.member_limit_per_slot
            if (active && limit && limit >= slot.bookings.length) active = false

            if (active && isPast(timestamp2date(slot.start_at))) active = false

            return active
        },
        showModal(slot) {
            this.modal.slot = slot
            this.modal.visible = true
        },
        hideModal() {
            this.modal.visible = false
            this.modal.slot = undefined
        },
        ...mapMutations({
            insertTeasers: 'currentPage/insertTeasers',
        }),
        ...mapActions('event', {
            fetchTeasers: 'fetchSlotsPaginated'
        }),
    },
    mounted() {
        this.load();
    }
}
</script>
